import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { marked } from 'marked';
import { RuleCreationChatService } from '../../../services/rule-creation-chat.service';
import {
  ArtifactStatusDto,
  ChatSseEvent,
  ChoiceOption,
  EvaluationConfigDto,
  ValidationDto,
  ValidatedPiPointInfoDto,
  SSE_EVENT_TYPES,
} from '../../../models/rule-creation-chat.model';

export interface DisplayMessage {
  role: 'user' | 'assistant';
  content: string;
  choices?: {
    prompt: string;
    options: ChoiceOption[];
    allowCustomInput: boolean;
  };
  isStreaming?: boolean;
}

@Component({
  selector: 'app-rule-creation-chat',
  templateUrl: './rule-creation-chat.component.html',
  styleUrl: './rule-creation-chat.component.scss',
  standalone: false,
})
export class RuleCreationChatComponent
  implements OnChanges, OnDestroy, AfterViewChecked
{
  @Input() equipmentId!: string;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() ruleId?: string;
  @Output() validatedPiPointsChange = new EventEmitter<
    ValidatedPiPointInfoDto[]
  >();
  @Output() evaluationConfigChange = new EventEmitter<EvaluationConfigDto>();
  @Output() validationChange = new EventEmitter<ValidationDto>();
  @Output() monitoringEquationChange = new EventEmitter<string>();
  @Output() activationConditionChange = new EventEmitter<string>();
  @ViewChild('messagesContainer')
  messagesContainer!: ElementRef<HTMLDivElement>;

  sessionId: string | null = null;
  artifacts: ArtifactStatusDto[] = [];
  messages: DisplayMessage[] = [];
  inputText = '';
  isLoading = false;
  isStarting = false;
  error: string | null = null;
  createdRuleId: string | null = null;

  private destroy$ = new Subject<void>();
  private shouldScroll = false;

  constructor(private chatService: RuleCreationChatService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mode === 'edit') {
      if (changes['ruleId'] && this.ruleId) {
        this.startSession();
      }
    } else {
      if (changes['equipmentId'] && this.equipmentId) {
        this.startSession();
      }
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isCompleted(): boolean {
    return this.artifacts.every((a) => a.isResolved);
  }

  get resolvedArtifactCount(): number {
    return this.artifacts.filter((a) => a.isResolved).length;
  }

  get canSend(): boolean {
    return (
      !!this.sessionId && !this.isLoading && this.inputText.trim().length > 0
    );
  }

  startSession(): void {
    this.isStarting = true;
    this.error = null;
    this.messages = [];
    this.sessionId = null;
    this.artifacts = [];
    this.createdRuleId = null;

    // Create a placeholder assistant message to receive the initial LLM response
    const assistantMessage: DisplayMessage = {
      role: 'assistant',
      content: '',
      isStreaming: true,
    };

    const session$ =
      this.mode === 'edit' && this.ruleId
        ? this.chatService.startModificationSession(this.ruleId)
        : this.chatService.startSession(this.equipmentId);

    session$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (event: ChatSseEvent) => {
        if (event.eventType === SSE_EVENT_TYPES.SESSION) {
          this.sessionId = event.sessionId ?? null;
          if (event.artifactStatus) {
            this.artifacts = event.artifactStatus;
          }
          this.isStarting = false;
          // Add the assistant message placeholder now that session is established
          this.messages.push(assistantMessage);
        } else {
          this.handleSseEvent(event, assistantMessage);
        }
      },
      error: (err) => {
        this.error = err?.message ?? 'Échec du démarrage de la session.';
        this.isStarting = false;
        this.isLoading = false;
      },
      complete: () => {
        assistantMessage.isStreaming = false;
        this.isLoading = false;
        this.shouldScroll = true;
      },
    });
  }

  sendMessage(): void {
    if (!this.canSend) return;

    const userText = this.inputText.trim();
    this.inputText = '';
    this.submitMessage(userText);
  }

  selectChoice(choiceId: string, choiceLabel: string): void {
    if (!this.sessionId || this.isLoading) return;
    this.submitMessage(choiceLabel, choiceId);
  }

  private submitMessage(text: string, selectedChoiceId?: string): void {
    this.messages.push({ role: 'user', content: text });
    this.shouldScroll = true;

    const assistantMessage: DisplayMessage = {
      role: 'assistant',
      content: '',
      isStreaming: true,
    };
    this.messages.push(assistantMessage);

    this.isLoading = true;
    this.error = null;

    const request = {
      sessionId: this.sessionId!,
      message: text,
      selectedChoiceId,
    };

    const message$ =
      this.mode === 'edit'
        ? this.chatService.streamModificationMessage(request)
        : this.chatService.streamMessage(request);

    message$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (event: ChatSseEvent) =>
        this.handleSseEvent(event, assistantMessage),
      error: (err) => {
        assistantMessage.isStreaming = false;
        assistantMessage.content =
          assistantMessage.content || 'Une erreur est survenue.';
        this.error = err?.message ?? 'Échec de la diffusion en continu.';
        this.isLoading = false;
        this.shouldScroll = true;
      },
      complete: () => {
        assistantMessage.isStreaming = false;
        this.isLoading = false;
        this.shouldScroll = true;
      },
    });
  }

  private handleSseEvent(event: ChatSseEvent, message: DisplayMessage): void {
    switch (event.eventType) {
      case SSE_EVENT_TYPES.TOKEN:
        if (event.content) {
          message.content += event.content;
          this.shouldScroll = true;
        }
        break;
      case SSE_EVENT_TYPES.CHOICES:
        if (event.choices) {
          message.choices = event.choices;
          this.shouldScroll = true;
        }
        break;
      case SSE_EVENT_TYPES.ARTIFACTS:
        if (event.artifactStatus) {
          this.artifacts = event.artifactStatus;
        }
        if (event.validatedPiPoints != null) {
          this.validatedPiPointsChange.emit(event.validatedPiPoints);
        }
        if (event.evaluationConfig) {
          this.evaluationConfigChange.emit(event.evaluationConfig);
        }
        if (event.validation) {
          this.validationChange.emit(event.validation);
        }
        if (event.monitoringEquation != null) {
          this.monitoringEquationChange.emit(event.monitoringEquation);
        }
        if (event.activationCondition != null) {
          this.activationConditionChange.emit(event.activationCondition);
        }
        break;
      case SSE_EVENT_TYPES.COMPLETED:
        if (event.createdRuleId) {
          this.createdRuleId = event.createdRuleId;
        }
        break;
    }
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      const el = this.messagesContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  renderMarkdown(content: string): string {
    if (!content) return '';
    return marked(content) as string;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
